import { useCallback, useState } from 'react';
import { useTheme } from 'styled-components/macro';
import { useAiEditor } from './hooks/use-ai-editor';
import { AiEditorProvider } from './ai-editor.context';
import { AiEditorProps } from './ai-editor.types';
import { Dialog, VStack } from 'components/ui';
import {
  AiEditorContent,
  AiEditorFooter,
  AiEditorHeader,
  AiEditorStyles,
  AiEditorTrigger,
  AnimateChangeInHeight,
} from './ui';
import { ContentWrapper } from './ai-editor.styled';

const AiEditorBase = (props: AiEditorProps) => {
  const { open: controlledOpen, onOpenChange, children } = props;

  const [internalOpen, setInternalOpen] = useState<boolean>(false);
  const isOpen = controlledOpen ?? internalOpen;

  const handleOpenChange = useCallback(
    (value: boolean) => {
      setInternalOpen(value);
      onOpenChange?.(value);
    },
    [onOpenChange]
  );

  const theme = useTheme();
  const state = useAiEditor({ ...props, open: isOpen });

  const handleClose = useCallback(() => {
    state.handleCancel();
    handleOpenChange(false);
  }, [handleOpenChange, state]);

  const handleApply = useCallback(() => {
    state.handleApply();
    handleOpenChange(false);
  }, [handleOpenChange, state]);

  return (
    <AiEditorProvider value={state}>
      <Dialog.Root open={isOpen} onOpenChange={handleOpenChange} modal>
        {children}

        <Dialog.Portal>
          <Dialog.Content onCloseAutoFocus={(e) => e.preventDefault()}>
            <Dialog.Panel style={{ width: 650 }}>
              <VStack padding={theme.spacings.base} spacing="s">
                <AiEditorHeader />

                <AnimateChangeInHeight>
                  <ContentWrapper>
                    <AiEditorStyles />
                    <AiEditorContent />
                  </ContentWrapper>
                </AnimateChangeInHeight>

                <AiEditorFooter onClose={handleClose} onApply={handleApply} />
              </VStack>
            </Dialog.Panel>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </AiEditorProvider>
  );
};

export const AiEditor = Object.assign(AiEditorBase, {
  Trigger: AiEditorTrigger,
});
